import {paymentPlanUpdatesApi} from '../api/payment-plan-updates';
import type {PaymentUpdateView} from '../dtos/payment-plan-update';
import {subscribePaymentUpdate} from '../utils/payment-plan-update-sync';
import { useCustomer } from "@/modules/clients/hooks/use-customer.hook";
import { Alert, Box, Button, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { BoardAction } from "../dtos";
import { useWorkflowBoard } from "../hooks/use-workflow-board.hook";
import { requestBoardFocus } from "../utils/board-focus-store";
import { closureSummary } from "../utils/closure-summary";
import { holdSummary } from "../utils/hold-summary";
import NextActionBanner from "./next-action-banner";
import UpcomingActionsModal from "./upcoming-actions-modal";
import {NoteAttachmentList} from "./note-attachments";

/**
 * §BannerAboveTabs: lead-page host for the next-action / flow-blocked banner, mounted ABOVE the tab
 * bar next to the orange start-flow banner (NawyNowLeadBanner) — so the loan process's current ask
 * is visible from every tab, not only from inside the Nawy Now one.
 *
 * Both queries it runs (customer + board) are the SAME react-query keys the tab itself uses, so this
 * shares their cache instead of adding requests. Selecting an action from the review modal switches
 * to the Nawy Now tab (`onOpenBoard`) and publishes a focus request the board consumes to expand and
 * scroll to that step.
 */
/** What the banner says while a submitted payment-plan revision is still working through its reviews. */
const revisionNotice = (
  revision: NonNullable<PaymentUpdateView['revision']>,
  outstanding: PaymentUpdateView['obligations']['steps']
): string => {
  if (revision.status === 'rejected') {
    return 'Payment-plan changes were requested. Sales must review the feedback and resubmit.';
  }
  if (outstanding.length) {
    return `Payment-plan reviews outstanding: ${outstanding.map(step => step.name).join(', ')}. Open a review to see its current prerequisites.`;
  }
  return 'The updated payment plan is waiting for its required documents and final checks.';
};

const NawyNowStatusBanner = ({
  leadUid,
  journeyUid,
  customerUId,
  onOpenBoard,
}: {
  leadUid?: string;
  journeyUid?: string;
  customerUId?: string;
  /** reveal the Nawy Now board (tab switch on desktop, drawer on mobile) */
  onOpenBoard?: () => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const { data: client, isFetched: clientFetched } = useCustomer({
    uid: customerUId,
  });
  const { hasCase, board, caseUid } = useWorkflowBoard({
    client,
    // fetched-or-nothing-to-fetch — the board waits for phones instead of double-fetching
    clientReady: !customerUId || clientFetched,
    journeyUid,
  });
  const [actionsOpen, setActionsOpen] = useState(false);
  const [paymentState, setPaymentState] = useState<{caseUid: string; view: PaymentUpdateView} | null>(null);
  const highestPayment = useRef<{caseUid: string; version: number} | null>(null);
  useEffect(() => {
    if (!caseUid) return;
    let active = true;
    const install = (view: PaymentUpdateView): void => {
      if (!active || (highestPayment.current?.caseUid === caseUid && view.version < highestPayment.current.version)) return;
      highestPayment.current = {caseUid, version: view.version};
      setPaymentState({caseUid, view});
    };
    void paymentPlanUpdatesApi.get(caseUid).then(install).catch(() => undefined);
    const unsubscribe = subscribePaymentUpdate(caseUid, install);
    return () => {active = false; unsubscribe();};
  }, [caseUid, board]);


  // No case yet ⇒ the orange start-flow banner owns this slot; nothing to announce here.
  if (!leadUid || !hasCase || !board) return null;

  const blockedStepName =
    board.phases
      .flatMap((phase) => phase.steps)
      .find((boardStep) => boardStep.uiStatus === "BLOCKER")?.step.stepName ??
    null;

  // §37 (QA): with nothing agent-actionable, the banner still names the flow's CURRENT step —
  // "Pending other parties" alone told the agent nothing about where the application stands.
  const currentStepName =
    board.phases
      .flatMap((phase) => phase.steps)
      .find((boardStep) => boardStep.step.status === "IN_PROGRESS")?.step.stepName ?? null;

  // §Flow-closed-note: a closed application announces WHY above the tabs — the closing reviewer's note (e.g.
  // Credit's Reject on DBR & Payment Plan) used to live on that WM step alone, where sales could not see it.
  // A closed flow has no next action either, so this too comes BEFORE the nothing-to-say check.
  if (board.closure) {
    return (
      <Box sx={{ px: { mobile: 2, tablet: 3 }, pt: 1.5 }}>
        <Box
          component="output"
          aria-label="Flow closed"
          sx={{
            display: "block",
            px: 1.5,
            py: 1,
            borderRadius: theme.nawy.radius.md,
            backgroundColor: "#F1F5F9",
            color: "#475569",
            fontSize: 13,
            lineHeight: "20px",
            fontWeight: 600,
            overflowWrap: "anywhere",
          }}
        >
          {closureSummary(board.closure)}
          <NoteAttachmentList attachments={board.closure.attachments} />
        </Box>
      </Box>
    );
  }

  // §Case-hold: a held application always announces itself above the tabs — a frozen flow has no next
  // action to speak of, so this must come BEFORE the nothing-to-say check below.
  if (board.onHold) {
    return (
      <Box sx={{ px: { mobile: 2, tablet: 3 }, pt: 1.5 }}>
        <Box
          component="output"
          sx={{
            display: "block",
            px: 1.5,
            py: 1,
            borderRadius: theme.nawy.radius.md,
            backgroundColor: "#FFF3CD",
            color: "#8A5A00",
            fontSize: 13,
            lineHeight: "20px",
            fontWeight: 600,
          }}
        >
          {holdSummary(board.onHold)}. Nothing can be changed until a Workflow Manager admin resumes it.
          <NoteAttachmentList attachments={board.onHold.attachments} />
        </Box>
      </Box>
    );
  }

  const revisionView = paymentState?.caseUid === caseUid ? paymentState.view : null;
  if (revisionView?.revision?.status === 'pending' || revisionView?.revision?.status === 'rejected') {
    const outstanding = revisionView.obligations.steps.filter(step => !['APPROVED', 'SKIPPED'].includes(step.status));
    return <Box sx={{px: 2, py: 1.5}}><Alert severity="info" action={<Button onClick={onOpenBoard}>View payment-plan reviews</Button>}>
      {revisionNotice(revisionView.revision, outstanding)}
    </Alert></Box>;
  }

  // Every case has SOME state, but with nothing to say the banner renders null anyway — keep the
  // wrapper out of the layout in that case so no empty padded strip sits above the tabs.
  // §NextForAgent: a coming agent step keeps the bar alive even when nothing is open for them.
  if (
    !board.nextAction &&
    !board.nextAgentAction &&
    !board.upcomingActions.length &&
    !blockedStepName
  ) {
    return null;
  }

  const selectAction = (action: BoardAction) => {
    setActionsOpen(false);
    onOpenBoard?.();
    requestBoardFocus(action);
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: 2,
        py: 1.5,
        backgroundColor: colors.semantic.layer.primary,
      }}
    >
      <NextActionBanner
        nextAction={board.nextAction}
        nextAgentAction={board.nextAgentAction}
        upcomingActions={board.upcomingActions}
        // §EditsNeeded: every sent-back step, for the composed "Update KYC, credit documents &
        // payment plan" red banner (kind EDITS on the urgent surface)
        editsTitles={board.urgentActions
          .filter((action) => action.kind === "EDITS")
          .map((action) => action.title)}
        // §EditsNeeded: WHAT is owed — the rejected file names / field labels off the board rows
        editsItems={board.phases
          .flatMap((phase) => phase.steps)
          .filter((boardStep) => boardStep.uiStatus === "EDITS_NEEDED")
          .flatMap((boardStep) => boardStep.step.editsNeededItems ?? [])}
        // §Conditional: same shape on the orange ramp — approved-with-conditions files
        conditionalTitles={board.urgentActions
          .filter((action) => action.kind === "CONDITIONAL")
          .map((action) => action.title)}
        conditionalItems={board.phases
          .flatMap((phase) => phase.steps)
          .flatMap((boardStep) => boardStep.step.conditionalItems ?? [])}
        blockedStepName={blockedStepName}
        currentStepName={currentStepName}
        onReview={() => setActionsOpen(true)}
      />
      <UpcomingActionsModal
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        nextAction={board.nextAction}
        nextAgentAction={board.nextAgentAction}
        urgentActions={board.urgentActions}
        upcomingActions={board.upcomingActions}
        onSelect={selectAction}
      />
    </Box>
  );
};

export default NawyNowStatusBanner;
